// assets/es-card-enhancements.js
// Two independent enhancements for the redesigned product cards (search + collection grids):
//
// 1) Rating resolution: the card's .es-card__rating-stars render server-side as gold SVG stars
//    for the static "4.9 Store Rating" fallback (see snippets/c-product-item-grid-v1.liquid/
//    v2.liquid -- same SVG star path used there and here, so shape/size are always identical;
//    only color + fill amount ever differ). The .jdgm-widget.jdgm-preview-badge div (matching
//    the same markup sections/product-details.liquid already uses on the product page) is kept
//    in the DOM purely as a hidden data source -- Judge.me's loader ignores the legacy
//    .shopify-product-reviews-badge selector entirely, confirmed via ?jdgm_debug=true console
//    output, so this is the real element it needs to populate. Once its
//    data-average-rating/data-number-of-reviews attributes are populated (polled, since this
//    happens asynchronously), a real review count > 0 re-renders these SAME stars from the real
//    average rating and recolors them blue via .is-real -- never a separate show/hide between
//    two different-looking star elements.
//
// 2) Selected-vehicle-aware fitment: the YMM selector (assets/vehicle-memory-bar.js) already
//    writes the shopper's chosen vehicle to localStorage.esSelectedVehicle as {label, url}.
//    Each card's .es-card__fits carries every compatible model in data-fits-all (pipe
//    separated, straight from the product title). If the selected vehicle's label matches one
//    of those models, that model is promoted to the front of the line and highlighted --
//    instead of always showing whichever model the title happened to list first.
(function () {
  var STORAGE_KEY = 'esSelectedVehicle';
  // Brand names are never a real trim differentiator WITHIN one product's own fitment list
  // (every row is the same brand) -- but only the first-listed model in a title typically
  // carries the brand prefix (e.g. "Volkswagen Golf MK7 / Golf Sportwagen MK7 / Golf R MK7"),
  // which artificially inflated that one candidate's token score and tied it with the actual
  // best match. Stripping brand words keeps scoring based purely on real trim/model tokens.
  var BRAND_STOPWORDS = ['volkswagen', 'vw', 'bmw', 'audi', 'porsche', 'mercedes', 'mercedes-benz', 'seat', 'skoda', 'mini'];

  function normalize(str) {
    return (str || '')
      .toLowerCase()
      .replace(/[()]/g, ' ')
      .replace(/[^a-z0-9.\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function tokenize(str) {
    // Keep single-character tokens (e.g. "R", "S", "M") -- they're meaningful trim codes,
    // not noise. Dropping them made "Golf R MK7" and plain "Golf MK7" indistinguishable.
    return normalize(str).split(' ').filter(function (t) {
      return t.length > 0 && BRAND_STOPWORDS.indexOf(t) === -1;
    });
  }

  function getSelectedVehicle() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && parsed.label ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function applyFitsMatching() {
    var vehicle = getSelectedVehicle();
    if (!vehicle) return;
    var vehicleTokens = tokenize(vehicle.label);
    if (!vehicleTokens.length) return;

    document.querySelectorAll('.es-card__fits[data-fits-all]').forEach(function (el) {
      if (el.dataset.fitsMatched === '1') return; // already resolved
      var models = el.getAttribute('data-fits-all').split('|').filter(Boolean);
      if (!models.length) return;
      // Products that only fit ONE model (single-fitment parts, common for chassis-specific
      // items) were never being checked at all here -- they still need the "your" wording +
      // blue highlight when that one model matches the shopper's selected vehicle, there's
      // just no "+N more" badge to show alongside it.

      var years = el.getAttribute('data-fits-years') || '';
      // Tag-based match: the selector filters by tags, so if this product carries every
      // model/generation tag of the remembered vehicle (and one of its years), it fits it --
      // show THAT vehicle even when the title lists other models first or omits it entirely.
      var vt = (vehicle.tags || []).map(function (t) { return String(t).toLowerCase(); });
      var vtYear = vt.filter(function (t) { return /^(19|20)\d\d$/.test(t); });
      var vtCore = vt.filter(function (t) { return !/^(19|20)\d\d$/.test(t); });
      var pt = (el.getAttribute('data-ptags') || '').split('|');
      if (vtCore.length && vtCore.every(function (t) { return pt.indexOf(t) !== -1; }) && (!vtYear.length || vtYear.some(function (t) { return pt.indexOf(t) !== -1; }))) {
        var lab = el.querySelector('.es-card__fits-label'), mb = el.querySelector('.es-card__fits-more');
        var oth = models.filter(function (m) { var mt = tokenize(m); return !(mt.length && mt.every(function (t) { return vehicleTokens.indexOf(t) !== -1; })); });
        // Widened selection (make+model tags only) with a longer label: the generation/year is not
        // confirmed by the tags, so never promise the full label -- ask them to confirm.
        var shortL = String(vehicle.label).split(/\s+/).filter(function (w, i, arr) { return arr.slice(0, i + 1).every(function (x) { return !/[0-9(]/.test(x); }); }).join(' ');
        var widenedSel = vt.length < 3 && shortL && shortL !== vehicle.label;
        if (lab) lab.textContent = widenedSel ? 'Confirm fitment for your ' + shortL : 'Fits your ' + vehicle.label;
        el.classList.add('is-matched'); el.dataset.fitsMatched = '1';
        if (oth.length) {
          var tip = 'Also fits: ' + oth.join(', ') + (years ? ' (' + years + ')' : '');
          el.setAttribute('title', tip);
          if (window.jQuery && jQuery.fn.tooltip) jQuery(el).attr('data-original-title', tip);
          if (mb) mb.textContent = '+' + oth.length;
        } else if (mb) { mb.textContent = ''; }
        return;
      }
      // Pick the MOST SPECIFIC full match, not the first one found -- e.g. a vehicle
      // "Golf R MK7" fully contains the tokens for a plain "Golf MK7" candidate too, so
      // scanning in title order would wrongly promote the base trim over the correct one.
      var matchIdx = -1;
      var matchScore = 0;
      for (var i = 0; i < models.length; i++) {
        var modelTokens = tokenize(models[i]);
        if (!modelTokens.length) continue;
        var allPresent = modelTokens.every(function (t) { return vehicleTokens.indexOf(t) !== -1; });
        if (allPresent && modelTokens.length > matchScore) {
          matchScore = modelTokens.length;
          matchIdx = i;
        }
      }
      if (matchIdx === -1) return;

      var label = el.querySelector('.es-card__fits-label');
      var moreBadge = el.querySelector('.es-card__fits-more');
      var matchedModel = models[matchIdx];
      var others = models.slice(0, matchIdx).concat(models.slice(matchIdx + 1));

      if (label) label.textContent = 'Fits your ' + matchedModel;
      el.classList.add('is-matched');
      el.dataset.fitsMatched = '1';

      if (others.length) {
        var tooltip = 'Also fits: ' + others.join(', ') + (years ? ' (' + years + ')' : '');
        el.setAttribute('title', tooltip);
        if (window.jQuery && jQuery.fn.tooltip) {
          jQuery(el).attr('data-original-title', tooltip);
        }
        if (moreBadge) moreBadge.textContent = '+' + others.length;
      }
    });
  }

  // Same path used in snippets/c-product-item-grid-v1.liquid/v2.liquid for the SSR'd fallback
  // stars -- keep these in sync if the shape ever changes.
  var STAR_PATH = 'M12 2 14.59 8.36 21.51 8.97 16.24 13.62 17.9 20.44 12 16.9 6.1 20.44 7.76 13.62 2.49 8.97 9.41 8.36 Z';

  function starsHTML(rating) {
    var html = '';
    for (var i = 0; i < 5; i++) {
      var fill = Math.max(0, Math.min(1, rating - i));
      var insetPct = Math.round((1 - fill) * 100);
      html += '<span class="es-star">' +
        '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="' + STAR_PATH + '" fill="currentColor" opacity="0.25"></path></svg>' +
        '<svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" style="clip-path: inset(0 ' + insetPct + '% 0 0)"><path d="' + STAR_PATH + '" fill="currentColor"></path></svg>' +
        '</span>';
    }
    return html;
  }

  function resolveRating(root) {
    if (root.dataset.ratingResolved === '1') return;
    var prevBadge = root.querySelector('.jdgm-prev-badge');
    if (!prevBadge) return; // Judge.me hasn't populated the hidden widget yet -- keep polling
    var reviewCount = parseInt(prevBadge.getAttribute('data-number-of-reviews') || '0', 10);
    if (!reviewCount) return; // genuinely zero reviews -- the SSR gold fallback stays as-is
    var avgRating = parseFloat(prevBadge.getAttribute('data-average-rating') || '0');
    var starsEl = root.querySelector('.es-card__rating-stars');
    var textEl = root.querySelector('.es-card__rating-text');
    if (starsEl) {
      starsEl.innerHTML = starsHTML(avgRating);
      starsEl.classList.add('is-real');
    }
    if (textEl) {
      // <strong> around the number to match the SSR fallback's "<strong>4.9</strong> Store
      // Rating" exactly -- the rating figure is always bold, real or fallback.
      textEl.innerHTML = '<strong>' + avgRating.toFixed(1) + '</strong> (' + reviewCount + (reviewCount === 1 ? ' review' : ' reviews') + ')';
    }
    root.dataset.ratingResolved = '1';
  }

  function scanRatings() {
    document.querySelectorAll('.es-card__rating').forEach(resolveRating);
  }

  // The base theme's own assets/engo-scripts.js runs
  // `$(".product-item .add_to_wishlist").each(function(){ $(this).tooltip({placement:"top", trigger:"hover"}) })`
  // completely independent of any data-toggle/data-placement attribute on the element -- so
  // removing those attributes from the card markup didn't stop it. Confirmed live: the
  // resulting Bootstrap tooltip renders ABOVE the icon, which sits near the card's own top
  // edge, and got clipped by .es-card's overflow:hidden (needed for the rounded corners) --
  // that's the "little black text bar that gets cut off" and the "reacts weird when clicked"
  // (the tooltip flashing into view right as the click navigates to /account/login). Can't
  // safely edit that shared base file (other non-card tooltips on the site may rely on it), so
  // this destroys the tooltip instance specifically on redesigned cards' wishlist icons right
  // after the base script attaches it, leaving the plain `title` attribute for accessibility.
  function destroyWishlistTooltips() {
    if (!window.jQuery || !jQuery.fn.tooltip) return;
    jQuery('.es-card .add_to_wishlist').tooltip('destroy');
  }

  function init() {
    applyFitsMatching();
    scanRatings();
    destroyWishlistTooltips();
  }

  document.addEventListener('DOMContentLoaded', init);

  // Judge.me's widget loads asynchronously; poll briefly (~7.5s) to catch it settling in,
  // then stop -- a MutationObserver isn't worth it here since this only needs to run once.
  // Also re-runs destroyWishlistTooltips() each tick during that window, since it isn't
  // guaranteed the base theme's script (which attaches the tooltip in the first place) has
  // already run by the time this script's own DOMContentLoaded handler fires. Same story for
  // applyFitsMatching(): assets/es-related-products.js can swap in a whole new set of cards
  // (the vehicle-aware "Latest Products" override) *after* this script's own DOMContentLoaded
  // handler already ran once, and those fresh cards would otherwise never get the "Fits your
  // X" highlight -- its own dataset.fitsMatched guard already makes repeat calls a no-op for
  // cards already resolved, so polling it here is free.
  var tries = 0;
  var pollId = setInterval(function () {
    applyFitsMatching();
    scanRatings();
    destroyWishlistTooltips();
    tries++;
    if (tries > 25) clearInterval(pollId);
  }, 300);
})();
